type ViewRenderer = (root: HTMLElement) => () => void;

const routes: Record<string, ViewRenderer> = {};
let rerender: (() => void) | null = null;

/**
 * How the next hash change should animate. Page moves slide like a native
 * push/pop (deeper routes come in from the right, going back slides them
 * out); a caller can ask for something else with navigate(), e.g. the
 * circle that grows out of the button when onboarding starts the countdown.
 */
export type Transition = { kind: "push" | "pop" | "none" } | { kind: "reveal"; x: number; y: number };
let pending: Transition | null = null;

const DEPTH: Record<string, number> = { "/": 0, "/settings": 1, "/edit": 2 };

export function registerRoute(path: string, renderer: ViewRenderer) {
  routes[path] = renderer;
}

/** Go to `hash` with a specific transition instead of the default slide. */
export function navigate(hash: string, transition: Transition) {
  pending = transition;
  if (location.hash === hash || (hash === "#/" && !location.hash)) {
    pending = null;
    rerender?.();
    return;
  }
  location.hash = hash;
}

type ViewTransitionDoc = Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void>; finished: Promise<void> } };

function run(transition: Transition, render: () => void) {
  const doc = document as ViewTransitionDoc;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!doc.startViewTransition || reduce || transition.kind === "none") {
    render();
    return;
  }
  const html = document.documentElement;
  html.dataset.nav = transition.kind;
  const vt = doc.startViewTransition(render);
  if (transition.kind === "reveal") {
    const { x, y } = transition;
    const r = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
    vt.ready
      .then(() => {
        html.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
          { duration: 700, easing: "cubic-bezier(.7,0,.2,1)", pseudoElement: "::view-transition-new(root)" }
        );
      })
      .catch(() => {});
  }
  vt.finished.finally(() => delete html.dataset.nav).catch(() => {});
}

export function startRouter(root: HTMLElement, fallback: string) {
  let cleanup: (() => void) | null = null;
  let path = "";

  function resolve(): string {
    const hash = location.hash.replace(/^#/, "") || "/";
    return routes[hash] ? hash : fallback;
  }

  function render() {
    cleanup?.();
    cleanup = null;
    path = resolve();
    cleanup = routes[path](root);
  }

  window.addEventListener("hashchange", () => {
    const from = path;
    const to = resolve();
    let transition = pending;
    pending = null;
    if (!transition) {
      const d = (DEPTH[to] ?? 1) - (DEPTH[from] ?? 1);
      transition = { kind: d > 0 ? "push" : d < 0 ? "pop" : "none" };
    }
    run(transition, render);
  });
  rerender = render;
  render();
}

/** Re-runs the current route's renderer, e.g. when the countdown's layout changes under it. */
export function refreshRoute() {
  rerender?.();
}

export function currentRoute(): string {
  return location.hash.replace(/^#/, "") || "/";
}
